/** The lifecycle path, in order. A draft can also leave it as `discarded`. */
export const EQMS_STATUSES = ['draft', 'in_review', 'approved', 'effective', 'obsolete'] as const;
export type EqmsStatus = (typeof EQMS_STATUSES)[number] | 'discarded';

export const EQMS_ACTIONS = [
  'register',
  'submit_review',
  'reject',
  'approve',
  'make_effective',
  'obsolete',
  'discard',
] as const;
export type EqmsAction = (typeof EQMS_ACTIONS)[number];

export type EqmsSignatureMeaning =
  | 'authorship'
  | 'responsibility'
  | 'review'
  | 'approval'
  | 'release'
  | 'obsolescence'
  | 'withdrawal';

export interface ControlledDocumentVersion {
  id: string;
  originalName: string;
  mimeType: string | null;
  size: number | null;
  content?: string | null;
  htmlContent?: string | null;
  authoringSnapshotId?: string | null;
  contentChecksum?: string | null;
  pdfChecksum?: string | null;
  createdAt?: string;
  version?: number;
}

export interface ControlledDocument {
  id: string;
  name: string;
  controlCode: string;
  lifecycleStatus: EqmsStatus;
  controlledVersion: string;
  trainingRequired?: boolean;
  sourceKind?: 'upload' | 'authored';
  effectiveVersionId?: string | null;
  effectiveControlledVersion?: string | null;
  revisionStatus?: EqmsStatus | null;
  revisionControlledVersion?: string | null;
  revisionVersionId?: string | null;
  department: string;
  ownerUserId: string;
  ownerName: string;
  approvedAt: string | null;
  effectiveAt: string | null;
  obsoleteAt: string | null;
  createdAt: string;
  updatedAt: string;
  currentVersion: ControlledDocumentVersion | null;
  /** Open, addressing and parked update requests; only ever non-zero for the owner. */
  pendingUpdateRequests?: number;
}

export interface EqmsSignature {
  id: string;
  documentId: string;
  userId: string;
  username: string;
  signerName: string;
  meaning: EqmsSignatureMeaning;
  reason: string;
  fromState: EqmsStatus | null;
  toState: EqmsStatus;
  signedAt: string;
  snapshotId?: string | null;
  sourceVersionId?: string | null;
  contentChecksum?: string | null;
  pdfChecksum?: string | null;
  idempotencyKey?: string | null;
}

export interface EqmsAuditEvent {
  logId: number;
  documentId: string;
  actionType: 'INSERT' | 'UPDATE' | 'READ' | 'DELETE';
  fieldName: string | null;
  userId: string | null;
  userName: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  timestamp: string;
}

export interface ControlledDocumentDetail {
  document: ControlledDocument;
  signatures: EqmsSignature[];
  auditEvents: EqmsAuditEvent[];
  authoring?: { draftId: string | null; canEdit: boolean; canCreateRevision: boolean };
  permissions?: { canApproveOwnDocument: boolean; canExcludeFromTraining?: boolean };
}

export interface EqmsSummary {
  total: number;
  byStatus: Record<EqmsStatus, number>;
  awaitingAction: number;
  permissions?: { canImportEffective?: boolean; canExcludeFromTraining?: boolean };
}

export interface ControlledDocumentsFilters {
  status?: EqmsStatus | 'all';
  search?: string;
  pageNumber: number;
  pageLength: number;
  sortKey: 'controlCode' | 'name' | 'status' | 'department' | 'ownerName' | 'updatedAt';
  sortDirection: 1 | -1;
}

export interface ControlledDocumentsResponse {
  documents: ControlledDocument[];
  total: number;
  pageNumber: number;
  pageLength: number;
}

export interface EqmsTransitionInput {
  action: EqmsAction;
  reason: string;
  meaning: EqmsSignatureMeaning;
  username: string;
  password: string;
  snapshotId?: string;
  expectedLockVersion?: number;
  idempotencyKey?: string;
  controlCode?: string;
  department?: string;
  trainingRequired?: boolean;
  importEffective?: boolean;
}

export const EQMS_UPDATE_REQUEST_STATUSES = ['open', 'addressing', 'parked', 'declined', 'done'] as const;
export type EqmsUpdateRequestStatus = (typeof EQMS_UPDATE_REQUEST_STATUSES)[number];
export type EqmsUpdateRequestDecision = Exclude<EqmsUpdateRequestStatus, 'open'>;
export const EQMS_PENDING_UPDATE_REQUEST_STATUSES = ['open', 'addressing', 'parked'] as const;
export type EqmsPendingUpdateRequestStatus = (typeof EQMS_PENDING_UPDATE_REQUEST_STATUSES)[number];

export interface EqmsUpdateRequest {
  id: string;
  documentId: string;
  requesterUserId: string;
  requesterName: string;
  documentVersion: string | null;
  sourceVersionId: string | null;
  reason: string;
  status: EqmsUpdateRequestStatus;
  decisionNote: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  resolvedVersion: string | null;
  createdAt: string;
  updatedAt: string;
  document: {
    id: string;
    name: string;
    controlCode: string | null;
    lifecycleStatus: EqmsStatus | null;
    controlledVersion: string | null;
    effectiveControlledVersion: string | null;
    revisionStatus: EqmsStatus | null;
    sourceKind: 'upload' | 'authored' | null;
    ownerName: string | null;
  };
  history: Array<{
    fromStatus: EqmsUpdateRequestStatus | null;
    toStatus: EqmsUpdateRequestStatus;
    note: string | null;
    actorName: string | null;
    createdAt: string;
  }>;
}

export interface EqmsUpdateRequestList {
  requests: EqmsUpdateRequest[];
  counts: Record<EqmsPendingUpdateRequestStatus, number>;
}

export interface EqmsUpdateRequestQuery {
  scope: 'owner' | 'mine';
  documentId?: string;
  status?: EqmsUpdateRequestStatus[];
}
